<template>
  <!-- 首頁供應商輪播：清單接兩份複本循環捲動；複本不給輔助技術重複朗讀、不可 tab 聚焦 -->
  <div
    ref="rail"
    class="original-provider-list"
    :class="{ 'is-static': reducedMotion }"
    @pointerenter="onPointerEnter"
    @pointerleave="hovering = false"
    @pointerdown="onPointerDown"
    @touchstart.passive="touching = true"
    @scroll.passive="onScroll"
  >
    <a
      v-for="(provider, index) in loopProviders"
      :key="index"
      href="#"
      :class="{ 'original-provider-copy': index >= providers.length }"
      :aria-hidden="index >= providers.length ? 'true' : null"
      :tabindex="index >= providers.length ? -1 : null"
      @click.prevent="$emit('select', provider)"
    >
      <img :src="provider.sidebar_image_app" :alt="provider.name">
    </a>
  </div>
</template>

<script>
const LOOP_COPIES = 2
const SCROLL_SPEED = 0.02 // 每毫秒捲動的 px
const RESUME_DELAY = 4000 // 使用者捲動（含 iOS 放開後的慣性滑動）停止後多久再自動捲動

const wrapOffset = (offset, cycle) => ((offset % cycle) + cycle) % cycle

export default {
  name: 'ProviderCarousel',
  props: {
    providers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      reducedMotion: false,
      hovering: false,
      touching: false
    }
  },
  computed: {
    loopProviders() {
      return Array.from({ length: LOOP_COPIES + 1 }).reduce(list => list.concat(this.providers), [])
    }
  },
  watch: {
    providers() {
      this.$nextTick(this.resetPosition)
    }
  },
  mounted() {
    this.visible = false
    this.resumeAt = 0
    this.lastFrame = 0
    this.position = 0
    this.appliedLeft = 0 // 程式最後一次設定後的 scrollLeft，用來分辨捲動是不是使用者造成的
    this.wasMoving = false

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.syncMotion()
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this.syncMotion)
    } else {
      this.motionQuery.addListener(this.syncMotion)
    }

    window.addEventListener('pointerup', this.release)
    window.addEventListener('pointercancel', this.release)
    window.addEventListener('touchend', this.release)
    window.addEventListener('touchcancel', this.release)

    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting
    })
    this.observer.observe(this.$refs.rail)

    this.frameId = requestAnimationFrame(this.animate)
  },
  beforeDestroy() {
    cancelAnimationFrame(this.frameId)
    if (this.motionQuery.removeEventListener) {
      this.motionQuery.removeEventListener('change', this.syncMotion)
    } else {
      this.motionQuery.removeListener(this.syncMotion)
    }
    window.removeEventListener('pointerup', this.release)
    window.removeEventListener('pointercancel', this.release)
    window.removeEventListener('touchend', this.release)
    window.removeEventListener('touchcancel', this.release)
    this.observer.disconnect()
  },
  methods: {
    syncMotion() {
      this.reducedMotion = this.motionQuery.matches
      this.resetPosition()
    },
    resetPosition() {
      const rail = this.$refs.rail
      if (rail) rail.scrollLeft = 0
      this.position = 0
      this.appliedLeft = rail ? rail.scrollLeft : 0
      this.wasMoving = false
      this.setOffset()
    },
    // 自動捲動以 --provider-shift（transform）位移，手機才不會把小數 scrollLeft 畫成不平均的 1px 跳動
    setOffset() {
      const rail = this.$refs.rail
      if (rail) rail.style.setProperty('--provider-shift', `${-this.position}px`)
    },
    onPointerEnter(event) {
      if (event.pointerType === 'mouse') this.hovering = true
    },
    // 觸控以 touch 事件判斷：iOS 開始滑動時就會送 pointercancel，手指其實還沒放開
    onPointerDown(event) {
      if (event.pointerType !== 'touch') this.touching = true
    },
    release(event) {
      if (event.pointerType === 'touch' || !this.touching) return
      this.touching = false
      this.pauseAutoScroll()
    },
    pauseAutoScroll() {
      this.resumeAt = performance.now() + RESUME_DELAY
    },
    // 捲動位置跟程式設定的不同＝使用者在捲（iOS 放開手指後的慣性滑動也會一直觸發），
    // 暫停自動捲動直到停止後 RESUME_DELAY，避免兩邊同時設定 scrollLeft 造成來回抖動
    onScroll() {
      if (Math.abs(this.$refs.rail.scrollLeft - this.appliedLeft) > 1) this.pauseAutoScroll()
    },
    // 滑鼠移入、觸控中、聚焦其中一項、畫面外或分頁隱藏時暫停，期間以使用者捲到的位置為準
    animate(now) {
      const rail = this.$refs.rail
      const elapsed = Math.min(now - (this.lastFrame || now), 50)
      this.lastFrame = now

      const canMove = this.visible && !document.hidden && !this.hovering && !this.touching &&
        !this.reducedMotion && now > this.resumeAt && !rail.contains(document.activeElement)
      const copyStart = rail.children[this.providers.length]

      if (canMove && copyStart) {
        // 一輪的寬度＝第一份複本第一項與原清單第一項的距離（含間距）
        const cycle = copyStart.offsetLeft - rail.children[0].offsetLeft
        if (cycle > 0) {
          // 觸控、滾輪仍是原生捲動；恢復自動捲動時把使用者捲的距離併進位移，scrollLeft 歸零
          if (!this.wasMoving) {
            this.position = wrapOffset(this.position + rail.scrollLeft, cycle)
            rail.scrollLeft = 0
            this.appliedLeft = rail.scrollLeft
          }
          this.position = wrapOffset(this.position + elapsed * SCROLL_SPEED, cycle)
          this.setOffset()
          this.wasMoving = true
        }
      } else {
        this.wasMoving = false
      }

      this.frameId = requestAnimationFrame(this.animate)
    }
  }
}
</script>
